import { PrimaryButton, TextInputField } from '@liveoptics/react-ui';
import { useRequest } from 'ahooks';
import React, { FormEvent, useCallback, useState } from 'react';
import { verifyEndUser } from '../../../../api/api';
import { Error, OpportunityMode, TheaterType, VerifyOwnerState } from '../../../../enums';
import { EmailCheckResponse } from '../../../../interfaces';
import TextButton from '../../../buttons/TextButton';
import { SalesforceAccountsAutoComplete } from '../../../form-fields';
import { CountriesAutoComplete } from '../../../form-fields/auto-complete/CountriesAutoComplete';
import SelectionOption from '../SelectionOption';
import Form from '../form/Form';

const ExistingCustomerForm = (props: {
  projectId: number;
  email: string;
  emailCheckResponse: EmailCheckResponse;
  defaultTheater: TheaterType;
  verifyOwnerStateCallback: (state: VerifyOwnerState, value?: unknown) => void;
  onEmailChange: (e: FormEvent<HTMLInputElement>) => void;
  onOptionChange: (option: SelectionOption) => void;
  onCancel: () => void;
  onSuccess: (state: VerifyOwnerState, email?: string) => void;
}) => {
  const [error, setError] = useState('');

  const verifyEndUserRequest = useRequest(verifyEndUser, {
    manual: true,
    debounceWait: 500,
  });

  const handleVerifyOwnerStateChange = useCallback(
    () => props.verifyOwnerStateCallback(VerifyOwnerState.SetOpportunity, props.emailCheckResponse),
    [props]
  );

  const handleSaveClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      setError('');
      verifyEndUserRequest
        .runAsync(props.projectId, {
          mode: OpportunityMode.None,
          ownerAccountId: props.emailCheckResponse.ExistingAccountId,
        })
        .then(() => {
          props.onSuccess(VerifyOwnerState.VerifyEndUser, props.email);
        })
        .catch(() => {
          setError(Error.VerifyEndUser);
        });
    },
    [verifyEndUserRequest, props]
  );

  return (
    <Form
      selectionOption={SelectionOption.Customer}
      error={error}
      renderActions={() => (
        <>
          <TextButton onClick={props.onCancel}>Cancel</TextButton>
          {props.emailCheckResponse?.CompanyName &&
          props.emailCheckResponse.SFAccountId !== '' &&
          props.emailCheckResponse.SFAccountId !== 'NA' &&
          props.emailCheckResponse.SFAccountId !== null ? (
            <PrimaryButton clickFn={handleVerifyOwnerStateChange}>Link SFDC ID</PrimaryButton>
          ) : (
            <PrimaryButton
              disabled={verifyEndUserRequest.loading}
              loading={verifyEndUserRequest.loading}
              loadingText="Loading..."
              clickFn={handleSaveClick}
            >
              Save
            </PrimaryButton>
          )}
        </>
      )}
      onChange={props.onOptionChange}
    >
      <TextInputField
        label=""
        autoComplete="on"
        type="email"
        placeholder="Enter email address"
        value={props.email}
        status="active"
        validationMessage={'Please enter a valid customer email address'}
        onChange={props.onEmailChange}
      />
      {props.emailCheckResponse.CompanyName ? (
        <SalesforceAccountsAutoComplete
          theater={props.defaultTheater}
          placeholder="Account Name / SFDC ID (Optional)"
          defaultValue={props.emailCheckResponse.CompanyName}
          disabled={true}
        />
      ) : (
        <></>
      )}
      <TextInputField
        label=""
        placeholder="First Name"
        defaultValue={props.emailCheckResponse.ContactFirstName}
        status="readonly"
      />
      <TextInputField
        label=""
        placeholder="Last Name"
        defaultValue={props.emailCheckResponse.ContactLastName}
        status="readonly"
      />
      <CountriesAutoComplete
        placeholder="Country"
        defaultValue={props.emailCheckResponse.CountryString}
        disabled={true}
      />
    </Form>
  );
};

export default ExistingCustomerForm;
